import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Seo } from "@/components/Seo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset — you can now sign in");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setSaving(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="bg-brand-subtle min-h-[70vh] flex items-center justify-center px-4 py-16">
      <Seo title="Reset Your Password | Central Air Depot" description="Choose a new password for your Central Air Depot account." noindex />
      <div className="bg-white border border-zinc-200 rounded-sm p-8 w-full max-w-md">
        <KeyRound className="w-8 h-8 text-brand mb-3" />
        <h1 className="font-heading font-black text-2xl text-brand-ink">Reset your password</h1>
        <p className="text-sm text-zinc-600 mt-2">Choose a new password for your account.</p>
        {!token ? (
          <p data-testid="reset-password-missing-token" className="mt-6 text-sm text-brand-red font-medium">
            This link is missing its reset code. Please use the exact link from your email, or <Link to="/login" className="underline">request a new one</Link>.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3">
            <Input data-testid="reset-password-input" type="password" required minLength={8} placeholder="New password"
              value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm" />
            <Input data-testid="reset-password-confirm-input" type="password" required minLength={8} placeholder="Confirm password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-sm" />
            {error && <p data-testid="reset-password-error" className="text-sm text-brand-red font-medium">{error}</p>}
            <button type="submit" disabled={saving} data-testid="reset-password-submit-btn"
              className="w-full bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold px-6 py-3 rounded-sm transition-colors duration-200 flex items-center justify-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Reset My Password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
